
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router";
import { useSceneStore } from "../stores/sceneStore";
import { api } from "../api";
import { evictAllInactive } from "../engine/modelCache";
import { meshEffectManager } from "../engine/effects/EffectManager";
import { recompileActiveSpace } from "../lib/recompileScene";

/**
 * Sandbox hook — bridges Zustand store + API client.
 * Returns data and actions for the SandboxApp component.
 * Keeps the URL route in sync with the active space.
 */
export function useSandbox() {
  const { slug } = useParams<{ slug: string }>();
  const [prompt, setPrompt] = useState("");
  const spaceLoadingRef = useRef<string | null>(null);

  // Preload asset metadata cache for the effect system (fire-and-forget)
  useEffect(() => {
    meshEffectManager.ensureCacheLoaded();
  }, []);

  const {
    setLoading,
    isLoading,
    loadingMessage,
    activeSpaceId,
    activeSpaceSlug,
    activeSpaceName,
    loadSpace,
    error,
    setError,
  } = useSceneStore();

  // Sync URL → store: when route slug changes, load the space
  useEffect(() => {
    if (!slug || slug === activeSpaceSlug) return;
    // Prevent duplicate loads
    if (slug === spaceLoadingRef.current) return;

    spaceLoadingRef.current = slug;
    setLoading(true, "Loading space...");

    // Fetch space metadata and resolve scene in parallel
    Promise.all([api.getSpace(slug), api.getSpaceScene(slug)])
      .then(([{ space, visibility }, { sceneDescriptor }]) => {
        loadSpace(space, sceneDescriptor, { visibility });
        setLoading(false);
        spaceLoadingRef.current = null;
        // Evict models from the previous space that are no longer active
        evictAllInactive();
      })
      .catch((err) => {
        console.error("Failed to load space:", err);
        setError(err instanceof Error ? err.message : "Failed to load space");
        setLoading(false);
        spaceLoadingRef.current = null;
      });
  }, [slug, activeSpaceSlug, loadSpace, setLoading, setError]);

  /** Handle AI generation from text prompt */
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isLoading || !activeSpaceSlug) return;

    const { applySpacePatch } = useSceneStore.getState();
    setLoading(true, "Crafting your world...");

    try {
      const response = await api.generate({
        spaceSlug: activeSpaceSlug,
        userInput: prompt.trim(),
      });

      applySpacePatch(response.spacePatch);
      await recompileActiveSpace();

      setPrompt("");
      setError(null);
    } catch (err) {
      console.error("Generation failed:", err);
      setError(err instanceof Error ? err.message : "Failed to generate scene");
    } finally {
      setLoading(false);
    }
  }, [prompt, isLoading, activeSpaceSlug, setLoading, setError]);

  return {
    // State
    prompt,
    isLoading,
    loadingMessage,
    activeSpaceId,
    activeSpaceSlug,
    activeSpaceName,
    error,

    // Actions
    setPrompt,
    setError,
    handleGenerate,
  };
}
